import type { MeetingTranscriptInfo, MeetingTranscriptStatus } from '@/types/meeting-transcripts';

/** Recommended FE poll interval after upload (`GET …/:id`). */
export const MATCHING_POLL_INTERVAL_MS = 4000;

/** Recommended FE poll interval after verification auto-generate (`GET …/:id`). */
export const GENERATION_POLL_INTERVAL_MS = 5000;

export const formatMeetingLabel = (info: Pick<MeetingTranscriptInfo, 'meetingDate' | 'meetingType'>): string => {
    const date = info.meetingDate;
    const type = info.meetingType?.trim();
    return type ? `${type} — (${date})` : date;
};

export const isMeetingTranscriptMatchingInFlight = (status: MeetingTranscriptStatus): boolean =>
    status === 'uploaded' || status === 'matching';

export const isMeetingTranscriptMatchingFailed = (status: MeetingTranscriptStatus): boolean => status === 'matching_failed';

export const isMeetingTranscriptCancelled = (status: MeetingTranscriptStatus): boolean => status === 'cancelled';

export const isMeetingTranscriptReadyForVerification = (status: MeetingTranscriptStatus): boolean => status === 'matching_complete';

export const isMeetingTranscriptGenerationInFlight = (status: MeetingTranscriptStatus): boolean => status === 'generating';

/** Keep calling `GET …/active` while matching or next-step generation is still running. */
export const shouldPollActiveMeetingTranscript = (status: MeetingTranscriptStatus): boolean =>
    isMeetingTranscriptMatchingInFlight(status) || isMeetingTranscriptGenerationInFlight(status);

/** Reject markers from `fetchActiveMeetingTranscript` for transient gateway/network blips. */
export const isTransientActiveJobFetchError = (error: unknown): boolean => {
    const message = typeof error === 'string' ? error : '';
    return /\b502\b/.test(message) || /bad gateway/i.test(message) || /network error/i.test(message);
};

const isMeetingTranscriptListPollingStatus = (status: MeetingTranscriptStatus): boolean =>
    status === 'matching' || status === 'generating' || status === 'generation_complete';

/** Keep refreshing the listing while any visible job is matching or generating. */
export const shouldPollMeetingTranscriptList = (
    activeJob: { status: MeetingTranscriptStatus } | null,
    historyItems: Array<{ status: MeetingTranscriptStatus }>
): boolean =>
    Boolean(activeJob && (shouldPollActiveMeetingTranscript(activeJob.status) || activeJob.status === 'generation_complete')) ||
    historyItems.some((item) => isMeetingTranscriptListPollingStatus(item.status));

/** Apply these poll results onto the matching history row by id. */
export const shouldApplyMeetingTranscriptListStatus = (status: MeetingTranscriptStatus): boolean =>
    status === 'matching_complete' || status === 'generation_failed' || status === 'generation_complete' || status === 'completed';

export const updateMeetingTranscriptInList = (items: MeetingTranscriptInfo[], job: MeetingTranscriptInfo): MeetingTranscriptInfo[] => {
    if (!shouldApplyMeetingTranscriptListStatus(job.status)) {
        return items;
    }
    let changed = false;
    const next = items.map((item) => {
        if (item.id !== job.id) {
            return item;
        }
        changed = true;
        return { ...item, ...job };
    });
    return changed ? next : items;
};

export const isMeetingTranscriptGenerationFailed = (status: MeetingTranscriptStatus): boolean =>
    status === 'generation_failed' || status === 'pdf_delivery_failed';

/** Failed jobs the upload screen can cancel or retry. */
export const isMeetingTranscriptRetryableFailure = (status: MeetingTranscriptStatus): boolean =>
    status === 'matching_failed' || status === 'generation_failed';

export const isMeetingTranscriptCompleted = (status: MeetingTranscriptStatus): boolean =>
    status === 'completed' || status === 'generation_complete';

export const isMeetingTranscriptPdfDownloadable = (status: MeetingTranscriptStatus): boolean => status === 'completed';

export const getMeetingTranscriptPdfFileName = (job: Pick<MeetingTranscriptInfo, 'meetingDate' | 'meetingType'>): string => {
    const type = job.meetingType?.trim().replace(/[^\w]+/g, '-') || 'meeting';
    return `${type}-${job.meetingDate}.pdf`;
};

export const getMatchingStatusMessage = (status: MeetingTranscriptStatus): string => {
    if (status === 'uploaded') {
        return 'File saved. Matching will start shortly.';
    }
    if (status === 'matching') {
        return 'Matching patients from the transcript…';
    }
    if (status === 'matching_failed') {
        return 'Matching failed.';
    }
    if (status === 'cancelled') {
        return 'This meeting transcript was cancelled.';
    }
    return '';
};

export const getGenerationStatusMessage = (status: MeetingTranscriptStatus): string => {
    if (status === 'generating') {
        return 'Generating next steps…';
    }
    if (status === 'generation_complete') {
        return 'Generation complete.';
    }
    if (status === 'generation_failed') {
        return 'Next-step generation failed.';
    }
    if (status === 'pdf_delivery_failed') {
        return 'Next steps were saved, but the PDF/email failed.';
    }
    return '';
};

export const getMeetingOverrideExpiryNote = (meetingDate: string): string =>
    `Overrides stay active through the end of the day after ${meetingDate} (hospital timezone).`;

export const getMeetingTranscriptStatusLabel = (status: MeetingTranscriptStatus): string => {
    switch (status) {
        case 'uploaded':
            return 'Matching pending';
        case 'matching':
            return 'Matching';
        case 'matching_complete':
            return 'Ready to verify';
        case 'matching_failed':
            return 'Matching failed';
        case 'generating':
            return 'Generating';
        case 'generation_complete':
            return 'Generation Complete';
        case 'generation_failed':
            return 'Generation failed';
        case 'completed':
            return 'Completed';
        case 'pdf_delivery_failed':
            return 'PDF failed';
        case 'cancelled':
            return 'Cancelled';
        default:
            return status;
    }
};

export const getMeetingTranscriptStatusColor = (status: MeetingTranscriptStatus): 'success' | 'warning' | 'error' | 'default' => {
    if (status === 'completed') {
        return 'success';
    }
    if (status === 'matching_complete' || status === 'generating' || status === 'generation_complete') {
        return 'warning';
    }
    if (status === 'matching_failed' || status === 'generation_failed' || status === 'pdf_delivery_failed') {
        return 'error';
    }
    return 'default';
};
